"use client";

import { Button } from "@/components/rac/button";
import { Sheet, SheetContent, SheetOverlay, SheetTrigger } from "@/components/rac/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/radix/avatar";
import { RelationshipDialog } from "@/components/relationship-dialog";
import { RelationshipToggle } from "@/components/relationship-toggle";
import { useAuth } from "@/hooks/use-auth";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { trackNavItem } from "@/utils/analytics/track-navigation";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";
import {
	BookOpen,
	Flag,
	HelpCircle,
	Home,
	LayoutDashboard,
	LogOut,
	Menu,
	Phone,
	Plus,
	Search,
	TableOfContents,
	User,
	Users,
	X,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React from "react";
import { getAvatarUrl, getInitials } from "../signin/utils";

interface MobileProfileMenuProps {
	className?: string;
}

export const MobileProfileMenu: React.FC<MobileProfileMenuProps> = ({ className }) => {
	const { isAuthenticated, user, logout, isLoading } = useAuth();
	const { openDialog } = useLoginDialog();
	const currentCityName = useCurrentCityName();
	const pathname = usePathname();
	const router = useRouter();

	const [showDialog, setShowDialog] = React.useState(false);
	const [pendingRelationship, setPendingRelationship] = React.useState<"buyer" | "seller" | null>(null);
	const [dialogType, setDialogType] = React.useState<"guard" | "selection">("guard");

	const handleLogout = async (close: () => void) => {
		try {
			await logout();
			close();
			router.push("/");
		} catch (error) {
			logger.error("Logout failed:", error);
		}
	};

	const handleShowDialog = (relationship: "buyer" | "seller", type: "guard" | "selection") => {
		setPendingRelationship(relationship);
		setDialogType(type);
		setShowDialog(true);
	};

	return (
		<>
			<Sheet>
				<SheetTrigger
					className={cn(
						"h-10 w-10 rounded-full hover:bg-gray-100 transition-colors duration-200 flex items-center justify-center bg-transparent border-0 cursor-pointer",
						className,
					)}
					aria-label="Open menu"
				>
					{isAuthenticated && user ? (
						<Avatar size="sm" className="h-8 w-8">
							<AvatarImage src={getAvatarUrl(user)} alt={user.name} />
							<AvatarFallback className="bg-primary text-white text-sm font-medium">
								{getInitials(user.name)}
							</AvatarFallback>
						</Avatar>
					) : (
						<Menu className="h-6 w-6" aria-hidden="true" />
					)}
					<span className="sr-only">Toggle menu</span>
				</SheetTrigger>

				<SheetOverlay className="z-[9999]">
					<SheetContent
						side="right"
						className="w-full max-w-[390px] p-0 bg-white/95 backdrop-blur-sm overflow-y-auto border-l-0 z-[10000]"
						closeButton={false}
					>
						{({ close }) => (
							<div className="flex flex-col min-h-screen">
								{/* Header Section with Gradient */}
								<div className="bg-gradient-to-b from-[oklch(var(--primary-600))] to-[oklch(var(--primary-400))] px-6 py-6 relative min-h-[280px]">
									{/* Header Controls */}
									<div className="flex justify-between items-center mb-6">
										{/* Relationship Toggle for authenticated users, route toggle for guests */}
										{isAuthenticated ? (
											<RelationshipToggle variant="compact" onAfterSwitch={close} onShowDialog={handleShowDialog} />
										) : (
											<div className="flex items-center bg-black/12 rounded-full p-[2px] relative">
												<div
													className={cn(
														"absolute top-[2px] w-[50px] h-[29px] bg-white rounded-full transition-transform duration-200",
														pathname === "/sell" ? "translate-x-[52px]" : "translate-x-[2px]",
													)}
												></div>
												<Link
													href="/buy"
													onClick={close}
													className={cn(
														"relative z-10 flex items-center justify-center h-[29px] px-3 rounded-full text-sm font-medium transition-colors",
														pathname === "/buy" || pathname === "/" ? "text-neutral-900" : "text-white",
													)}
												>
													Buy
												</Link>
												<Link
													href="/sell"
													onClick={close}
													className={cn(
														"relative z-10 flex items-center justify-center h-[29px] px-3 rounded-full text-sm font-medium transition-colors",
														pathname === "/sell" ? "text-neutral-900" : "text-white",
													)}
												>
													Sell
												</Link>
											</div>
										)}

										{/* Actions */}
										<div className="flex items-center gap-2 relative right-[-10px]">
											{/* Post Property Button */}
											{isAuthenticated ? (
												<Link
													href="/sell"
													onClick={close}
													className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors duration-200"
												>
													<Plus className="h-4 w-4" />
													<span>Post Property</span>
												</Link>
											) : (
												<div
													className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors duration-200 h-auto cursor-pointer"
													onClick={() => openDialog()}
												>
													<Plus className="h-4 w-4" />
													<span>Post Property</span>
												</div>
											)}
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8 text-white hover:text-gray-20"
												aria-label="Close menu"
												onPress={close}
											>
												<X className="h-5 w-5" />
											</Button>
										</div>
									</div>

									{/* User Profile Section */}
									{isAuthenticated && user ? (
										<div className="flex flex-col items-center text-center mb-6">
											<div className="w-[82px] h-[82px] bg-white rounded-full mb-4 flex items-center justify-center overflow-hidden">
												<Avatar size="xl" className="w-full h-full border-0">
													<AvatarImage src={getAvatarUrl(user)} alt={user.name} />
													<AvatarFallback className="bg-white text-primary text-xl font-semibold w-full h-full">
														{getInitials(user.name)}
													</AvatarFallback>
												</Avatar>
											</div>
											<div className="w-[170px]">
												<h2 className="text-lg font-medium text-neutral-900 mb-1 font-sans">
													Hello, {user.name || "User"}
												</h2>
												<p className="text-sm text-neutral-800/70 font-sans">
													{user.phones?.[0]?.number || "+91 XXXXXXXXXX"}
												</p>
											</div>
										</div>
									) : !isLoading ? (
										<div className="flex flex-col items-center text-center mb-6">
											<div className="w-[82px] h-[82px] bg-white rounded-full mb-4 flex items-center justify-center">
												<User className="w-8 h-8 text-primary" />
											</div>
											<div className="w-[170px]">
												<h2 className="text-lg font-medium text-neutral-900 mb-1 font-dm-sans">Welcome</h2>
												<p className="text-sm text-neutral-800/70 font-dm-sans">Log in to continue</p>
											</div>
										</div>
									) : (
										<div className="flex flex-col items-center text-center mb-6">
											<div className="w-[82px] h-[82px] bg-white/20 rounded-full mb-4 animate-pulse"></div>
											<div className="w-[170px] space-y-2">
												<div className="w-32 h-6 bg-white/20 rounded mb-1 animate-pulse mx-auto"></div>
												<div className="w-24 h-4 bg-white/20 rounded animate-pulse mx-auto"></div>
											</div>
										</div>
									)}
								</div>

								{/* Menu Content */}
								<div className="flex-1 px-6 py-4 bg-white">
									{!isLoading ? (
										<>
											{/* Login Section for unauthenticated users */}
											{!isAuthenticated && (
												<div className="mb-6">
													<div className="text-center">
														<div
															className="w-full h-12 rounded-lg font-medium text-base inline-flex items-center justify-center whitespace-nowrap bg-primary text-primary-foreground shadow-lg cursor-pointer hover:bg-primary/90 transition-colors"
															onClick={() => openDialog()}
														>
															Log in
														</div>
													</div>
												</div>
											)}

											{/* Profile Section - Always show, but handle auth when clicked */}
											<div className="mb-6">
												<MenuSection>
													<MenuItem
														href={isAuthenticated ? "/profile" : undefined}
														icon={Home}
														onClick={isAuthenticated ? close : () => openDialog()}
														trackingAction="profile-home"
													>
														Profile
													</MenuItem>
													{isAuthenticated && (
														<MenuItem
															href="/dashboard"
															icon={LayoutDashboard}
															onClick={close}
															trackingAction="dashboard"
														>
															Dashboard
														</MenuItem>
													)}
													<MenuItem
														href={isAuthenticated ? "/preference" : `/${currentCityName}`}
														icon={Search}
														onClick={close}
														trackingAction="preferences"
													>
														Resume Search
													</MenuItem>
												</MenuSection>
											</div>

											{/* Add Property Section */}
											<div className="mb-6">
												<MenuSection>
													<MenuItem
														href={isAuthenticated ? "/sell" : undefined}
														icon={Plus}
														onClick={isAuthenticated ? close : () => openDialog()}
														badge="Free"
														trackingAction="add-property"
														trackingCategory="cta"
													>
														Add a property to sell
													</MenuItem>
												</MenuSection>
											</div>

											{/* Our Story Section */}
											<div className="mb-6">
												<SectionTitle>Our Story</SectionTitle>
												<MenuSection>
													<MenuItem href="/about" icon={Users} onClick={close} trackingAction="about">
														About Us
													</MenuItem>
													<MenuItem href="/pricing" icon={TableOfContents} onClick={close} trackingAction="pricing">
														Pricing and Services
													</MenuItem>
													<MenuItem href="/faq" icon={HelpCircle} onClick={close} trackingAction="faq">
														FAQ
													</MenuItem>
													<MenuItem href="/blog" icon={BookOpen} onClick={close} trackingAction="blog">
														Blog
													</MenuItem>
												</MenuSection>
											</div>

											{/* Contact Section */}
											<div className="mb-6">
												<SectionTitle>Contact</SectionTitle>
												<MenuSection>
													<MenuItem href="/about" icon={Flag} onClick={close} trackingAction="report-bug">
														Report a bug
													</MenuItem>
													<MenuItem href="/about" icon={Phone} onClick={close} trackingAction="contact">
														Contact Us
													</MenuItem>
													{isAuthenticated && (
														<MenuItem
															onClick={() => handleLogout(close)}
															icon={LogOut}
															className="text-error-600 hover:text-error-700"
															isButton
															trackingAction="logout"
														>
															Log Out
														</MenuItem>
													)}
												</MenuSection>
											</div>
										</>
									) : (
										<div className="space-y-4">
											{Array.from({ length: 6 }).map((_, i) => (
												<div key={i} className="flex items-center gap-3 p-2">
													<div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
													<div className="flex-1 h-5 bg-gray-200 rounded animate-pulse"></div>
												</div>
											))}
										</div>
									)}
								</div>

								{/* Footer */}
								<div className="px-6 py-3 text-xs text-neutral-600 border-t border-neutral-200 bg-white">
									<div className="text-center">Settlin &copy; {new Date().getFullYear()} • v1.2.6</div>
								</div>
							</div>
						)}
					</SheetContent>
				</SheetOverlay>
			</Sheet>

			{showDialog && pendingRelationship && user && (
				<RelationshipDialog
					isOpen={showDialog}
					onClose={() => {
						setShowDialog(false);
						setPendingRelationship(null);
					}}
					currentRelationship={user.relation as "buyer" | "seller"}
					requiredRelationship={dialogType === "guard" ? pendingRelationship : undefined}
					targetRelationship={dialogType === "selection" ? pendingRelationship : undefined}
					featureName={`${pendingRelationship === "seller" ? "Seller" : "Buyer"} Features`}
					description={`Switch to ${pendingRelationship} mode to access ${pendingRelationship}-specific features`}
				/>
			)}
		</>
	);
};

// Helper Components
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<h3 className="text-sm font-bold text-neutral-900 mb-2 font-dm-sans leading-[21px]">{children}</h3>
);

const MenuSection: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="space-y-0 rounded-xl overflow-hidden bg-white border border-neutral-100 divide-y divide-neutral-100">
		{children}
	</div>
);

interface MenuItemProps {
	children: React.ReactNode;
	href?: string;
	icon: React.ComponentType<{ className?: string }> | (() => React.ReactNode);
	onClick?: () => void;
	className?: string;
	badge?: string;
	isButton?: boolean;
	trackingAction?: string;
	trackingCategory?: string;
}

const MenuItem: React.FC<MenuItemProps> = ({
	children,
	href,
	icon: Icon,
	onClick,
	className,
	badge,
	isButton = false,
	trackingAction,
	trackingCategory = "mobile-navigation",
}) => {
	const content = (
		<>
			<div className="flex items-center gap-2 w-5 h-5">
				<Icon className="w-5 h-5" />
			</div>
			<span className="flex-1 text-sm font-medium">{children}</span>
			{badge && <span className="px-2 py-1 text-xs font-medium text-white bg-success-600 rounded">{badge}</span>}
		</>
	);

	const handleClick = () => {
		// Track navigation if tracking action is provided
		if (trackingAction) {
			trackNavItem({
				action: trackingAction,
				category: trackingCategory,
			});
		}
		onClick?.();
	};

	const baseClasses = cn(
		"flex items-center gap-2 px-2 py-3 transition-colors duration-200",
		"hover:bg-neutral-50 active:bg-neutral-100",
		"text-neutral-900 font-dm-sans font-medium text-sm leading-[21px]",
		className,
	);

	if (isButton) {
		return (
			<button onClick={handleClick} className={baseClasses}>
				{content}
			</button>
		);
	}

	if (href) {
		return (
			<Link href={href} onClick={handleClick} className={baseClasses}>
				{content}
			</Link>
		);
	}

	return (
		<button onClick={handleClick} className={baseClasses}>
			{content}
		</button>
	);
};
